import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";
import { formatCurrencyToLocale, formatQuantityToLocale } from "../../../../utils/method";
import {
    ELEMENT_TYPES,
    ELEMENT_TYPE_BADGE_LABELS,
} from "../constants/boqElementTypes";
import {
    EditableDescriptionCell,
    EditableNumberCell,
    EditableOZCell,
} from "./BoQEditableCells";
import { BoQDragHandle } from "./BoQDragDrop";
import BoQLongTextEditor from "./BoQLongTextEditor";
import { TotalPriceCell, TotalPriceGrossCell } from "./BoQPriceCells";
import { UnitTagDropdown } from "./UnitTagDropdown";

/**
 * Returns the TanStack Table column definitions for the BoQ grid.
 * Extracted from BoQGrid so the main component stays lean.
 */
export function useBoQColumns({
    t,
    stableOnRowChange,
    effectiveEditingRowId,
    readOnly,
    unitOptionsRef,
    handleUnitTagChangeRef,
    editInputRef,
    setDraftValues,
    /** Ref holding a Set<string> of row ids that have an OZ validation error. */
    ozErrorRowIdsRef,
}) {
    return useMemo(
        () => [
            {
                id: "drag",
                header: () => t("boq.columns.drag"),
                size: 36,
                cell: ({ row }) => <BoQDragHandle rowId={row.original.id} readOnly={readOnly} />,
            },
            {
                id: "expand",
                header: () => "",
                size: 32,
                cell: ({ row }) => (
                    <span className="boq-grid-expand-cell">
                        {row.getCanExpand() ? (
                            <button
                                type="button"
                                className="boq-grid-expand-btn"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    row.toggleExpanded();
                                }}
                                aria-expanded={row.getIsExpanded()}
                            >
                                <FontAwesomeIcon
                                    icon={row.getIsExpanded() ? faChevronDown : faChevronRight}
                                />
                            </button>
                        ) : (
                            <span className="boq-grid-expand-placeholder" />
                        )}
                    </span>
                ),
            },
            {
                id: "itemNumber",
                accessorKey: "itemNumber",
                header: () => t("boq.columns.oz"),
                size: 140,
                cell: ({ row, getValue }) => {
                    const type = row.original?.type;
                    // Notes and additional_text never carry an OZ - show nothing.
                    const isNumberable =
                        type !== ELEMENT_TYPES.NOTE &&
                        type !== ELEMENT_TYPES.ADDITIONAL_TEXT;
                    const hasError =
                        ozErrorRowIdsRef?.current?.has(row.original.id) ?? false;
                    const isEditing = effectiveEditingRowId === row.original.id;

                    if (!isEditing || !isNumberable || readOnly) {
                        return (
                            <span className={hasError ? "boq-oz-cell--error" : undefined}>
                                {isNumberable ? (getValue() ?? null) : null}
                                {hasError && (
                                    <span
                                        className="boq-oz-error-badge"
                                        title={t("boq.oz-error-badge-title")}
                                        aria-label={t("boq.oz-error-badge-title")}
                                    >
                                        ⚠
                                    </span>
                                )}
                            </span>
                        );
                    }

                    return (
                        <EditableOZCell
                            row={row}
                            onRowChange={stableOnRowChange}
                            hasError={hasError}
                        />
                    );
                },
            },
            // ── Type badge column ────────────────────────────────────────────────
            {
                id: "elementType",
                header: () => t("boq.columns.type"),
                size: 110,
                cell: ({ row }) => {
                    const rowType = row.original?.type;
                    const badgeLabel = rowType ? t(ELEMENT_TYPE_BADGE_LABELS[rowType]) : null;
                    if (!badgeLabel) return null;
                    return (
                        <span className={`boq-type-badge boq-type-badge--${rowType.replace(/_/g, "-")}`}>
                            {badgeLabel}
                        </span>
                    );
                },
            },
            {
                id: "shortText",
                accessorKey: "shortText",
                header: () => t("boq.columns.description"),
                size: 700,
                minSize: 180,
                cell: ({ row }) => {
                    const isEditing = effectiveEditingRowId === row.original.id;
                    const isPosition = row.original?.type === ELEMENT_TYPES.POSITION;
                    const isNote = row.original?.type === ELEMENT_TYPES.NOTE;
                    const isAdditionalText = row.original?.type === ELEMENT_TYPES.ADDITIONAL_TEXT;
                    const showLongText = isPosition || isNote || isAdditionalText;
                    return (
                        <div key={row.original.id} className="boq-grid-description-cell">
                            <EditableDescriptionCell
                                row={row}
                                isEditing={isEditing}
                                onRowChange={stableOnRowChange}
                                editInputRef={editInputRef}
                                persistOnlyOnBlur={showLongText}
                                placeholder={t("boq.short-text-placeholder")}
                            />
                            {showLongText && isEditing && (
                                <BoQLongTextEditor
                                    key={`${row.original.id}-long`}
                                    value={row.original.longText ?? ""}
                                    onChange={(html) =>
                                        stableOnRowChange?.(row.original.id, { longText: html })
                                    }
                                    placeholder={t("boq.long-text-placeholder")}
                                />
                            )}
                        </div>
                    );
                },
            },
            {
                id: "quantity",
                accessorKey: "quantity",
                header: () => t("boq.columns.quantity"),
                size: 140,
                meta: { align: "right" },
                cell: ({ row, getValue }) => {
                    if (row.original?.type !== ELEMENT_TYPES.POSITION) {
                        const v = getValue();
                        return v != null ? formatQuantityToLocale(v) : null;
                    }
                    const isEditing = effectiveEditingRowId === row.original.id;
                    if (!isEditing) {
                        const v = getValue();
                        return v != null ? formatQuantityToLocale(v) : formatQuantityToLocale(0);
                    }
                    return (
                        <EditableNumberCell
                            key={`${row.original.id}-quantity`}
                            row={row}
                            field="quantity"
                            onRowChange={stableOnRowChange}
                            decimalPlaces={3}
                            onDraftChange={(v) => setDraftValues((p) => ({ ...p, quantity: v }))}
                        />
                    );
                },
            },
            {
                id: "unitTag",
                accessorKey: "unitTag",
                header: () => t("boq.columns.unit"),
                size: 180,
                cell: ({ row }) => {
                    if (row.original?.type !== ELEMENT_TYPES.POSITION) {
                        return row.original?.unitTag ?? null;
                    }
                    const isEditing = effectiveEditingRowId === row.original.id;
                    if (!isEditing) {
                        return row.original?.unitTag ?? null;
                    }
                    return (
                        <UnitTagDropdown
                            row={row}
                            unitOptionsRef={unitOptionsRef}
                            handleUnitTagChangeRef={handleUnitTagChangeRef}
                        />
                    );
                },
            },
            {
                id: "unitPrice",
                accessorKey: "unitPrice",
                header: () => t("boq.columns.unit-price-eur"),
                size: 150,
                meta: { align: "right" },
                cell: ({ row, getValue }) => {
                    if (row.original?.type !== ELEMENT_TYPES.POSITION) {
                        const v = getValue();
                        return v != null ? formatCurrencyToLocale(v) : null;
                    }
                    const isEditing = effectiveEditingRowId === row.original.id;
                    if (!isEditing) {
                        const v = getValue();
                        return v != null ? formatCurrencyToLocale(v) : null;
                    }
                    return (
                        <EditableNumberCell
                            key={`${row.original.id}-unitPrice`}
                            row={row}
                            field="unitPrice"
                            onRowChange={stableOnRowChange}
                            decimalPlaces={2}
                            suffix="€"
                            onDraftChange={(v) => setDraftValues((p) => ({ ...p, unitPrice: v }))}
                        />
                    );
                },
            },
            {
                id: "totalPrice",
                accessorKey: "totalPrice",
                header: () => t("boq.columns.total-net-eur"),
                size: 150,
                meta: { align: "right" },
                cell: ({ row }) => <TotalPriceCell row={row} />,
            },
            {
                id: "taxRate",
                accessorKey: "taxRate",
                header: () => t("boq.columns.vat-percent"),
                size: 150,
                meta: { align: "right" },
                cell: ({ row, getValue }) => {
                    // taxRate is stored as decimal (0.19). Multiply by 100 for display.
                    const decimal = getValue();
                    const pct = decimal != null ? decimal * 100 : null;
                    if (row.original?.type !== ELEMENT_TYPES.POSITION) {
                        return pct != null ? `${Number(pct)} %` : null;
                    }
                    return pct != null ? `${Number(pct)} %` : "0 %";
                },
            },
            {
                id: "totalPriceGross",
                accessorKey: "totalPriceGross",
                header: () => t("boq.columns.total-gross-eur"),
                size: 160,
                meta: { align: "right" },
                cell: ({ row }) => <TotalPriceGrossCell row={row} />,
            },
        ],
        [
            t,
            stableOnRowChange,
            effectiveEditingRowId,
            readOnly,
            editInputRef,
            handleUnitTagChangeRef,
            ozErrorRowIdsRef,
            setDraftValues,
            unitOptionsRef,
        ],
    );
}
